import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!-- Usamos el elemento nativo <dialog> para garantizar que esté en el "Top Layer" -->
    <!-- Esto hace que esté SOBRE TODO el layout (incluyendo sidebar, navbar, etc.) -->
    <dialog 
      #dialogElement 
      class="p-0 border-0 bg-transparent backdrop:bg-slate-950/40 dark:backdrop:bg-black/60 backdrop:backdrop-blur-sm open:flex items-center justify-center w-full h-full max-w-none max-h-none m-0"
      (click)="closeOnBackdrop($event)"
      (close)="onNativeClose()">
      
      <div class="relative w-full p-4 sm:p-0 mx-auto animate-modal-up" 
           [ngClass]="{
             'max-w-sm': size === 'sm',
             'max-w-md': size === 'md',
             'max-w-lg': size === 'lg',
             'max-w-xl': size === 'xl',
             'max-w-2xl': size === '2xl'
           }">
        <!-- Contenedor del Modal -->
        <div class="relative transform rounded-[2rem] bg-surface dark:bg-[#0d1f38] text-left shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] border border-foreground/10 sm:w-full">
          
          <!-- Content -->
          <div class="px-6 pb-6 pt-7 sm:px-10 sm:pb-10">
            <div class="flex flex-col">
              <div class="flex items-center gap-3 mb-4 border-b border-foreground/[0.03] pb-3">
                <div *ngIf="icon" class="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0 transition-all shadow-sm">
                    <iconify-icon [icon]="icon" width="18"></iconify-icon>
                </div>
                <h3 class="text-lg md:text-xl font-black leading-tight text-foreground uppercase tracking-tight whitespace-nowrap overflow-hidden text-ellipsis flex-1" id="modal-title">
                  {{ title }}
                </h3>
              </div>
              
              <div class="relative">
                <ng-content></ng-content>
              </div>
            </div>
          </div>

          <!-- Actions Footer -->
          <div *ngIf="showFooter" class="bg-foreground/[0.02] px-6 py-5 sm:px-10 sm:flex sm:flex-row-reverse gap-4 border-t border-foreground/[0.03] rounded-b-[2rem]">
            <ng-content select="[actions]"></ng-content>
          </div>
        </div>
      </div>
    </dialog>
  `,
  styles: [`
    :host { display: contents; }
    
    dialog::backdrop {
      /* Estilos para el fondo oscuro */
      opacity: 0;
      transition: opacity 0.3s ease-out;
    }
    
    dialog[open]::backdrop {
      opacity: 1;
    }

    dialog {
        outline: none;
    }

    .animate-modal-up {
        animation: modalFadeUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    @keyframes modalFadeUp {
      from { opacity: 0; transform: translateY(40px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  `]
})
export class AppModal implements OnChanges {
  @Input() isOpen = false;
  @Input() title = '';
  @Input() icon = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' | '2xl' = 'md';
  @Input() showFooter = true;
  @Output() isOpenChange = new EventEmitter<boolean>();

  get sizeClasses(): string {
    const map = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '2xl': 'max-w-2xl'
    };
    return map[this.size] || 'max-w-md';
  }

  @ViewChild('dialogElement') dialog!: ElementRef<HTMLDialogElement>;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && !changes['isOpen'].firstChange) {
      this.toggleDialog();
    }
  }

  ngAfterViewInit() {
    if (this.isOpen) {
      this.toggleDialog();
    }
  }

  private toggleDialog() {
    if (!this.dialog) return;

    const dialog = this.dialog.nativeElement;
    if (this.isOpen) {
      if (!dialog.open) {
        dialog.showModal();
        document.body.style.overflow = 'hidden';
      }
    } else {
      if (dialog.open) {
        dialog.close();
        document.body.style.overflow = '';
      }
    }
  }

  close() {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  closeOnBackdrop(event: MouseEvent) {
    if (event.target === this.dialog.nativeElement) {
      this.close();
    }
  }

  onNativeClose() {
    this.close();
  }
}
